// ──────────────────────────────────────────────────────────────
//  dashboardTemplates.ts — Pre-defined dashboard pages an admin can
//  add with one click.
//
//  Each template is a curated grid built from real metric keys in the
//  server's registry. The backend re-validates on save — an unknown key
//  is dropped on read and rejected on save — but the failure an admin
//  would see is a template that silently comes back short. The server's
//  `Tests/Analytics/DashboardTemplates.test.ts` reads this file and
//  pins every key it names against the live registry, so a metric
//  renamed on the server breaks the build instead.
//
//  compare/sparkline only appear on time-series metrics; a snapshot
//  total has no previous period to compare against.
// ──────────────────────────────────────────────────────────────

import type { DashboardWidget } from '../AppTypes';

/** A widget in a template — everything a real widget has except the instance-generated id. */
export type TemplateWidget = Omit<DashboardWidget, 'id'>;

/** A pre-defined dashboard page. */
export interface DashboardTemplate {
	/** A stable id for the template. */
	id: string;
	/** The tab name applied when the template is added. */
	name: string;
	/** A one-line description shown in the picker. */
	description: string;
	/** A Font Awesome icon class shown in the picker. */
	icon: string;
	/** The column count of the created tab. */
	columnCount: number;
	/** The widgets, in display order. */
	widgets: TemplateWidget[];
}

// ── Widget factories, to keep the templates below terse ────────

/**
 * Builds a single-value widget.
 *
 * @param metric - The metric key.
 * @param title  - The display title.
 * @param extra  - Any overrides.
 * @returns The template widget.
 */
function value(metric: string, title: string, extra: Partial<TemplateWidget> = {}): TemplateWidget {
	return { viz: 'value', metric, title, span: 1, rangeDays: 90, ...extra };
}

/**
 * Builds a chart widget.
 *
 * @param metric - The metric key.
 * @param title  - The display title.
 * @param extra  - Any overrides.
 * @returns The template widget.
 */
function chart(metric: string, title: string, extra: Partial<TemplateWidget> = {}): TemplateWidget {
	return { viz: 'chart', metric, title, span: 1, chartType: 'bar', granularity: 'day', rangeDays: 90, ...extra };
}

/**
 * Builds a multi-metric chart widget.
 *
 * @param metrics - The metric keys, overlaid as separate series.
 * @param title   - The display title.
 * @param extra   - Any overrides.
 * @returns The template widget.
 */
function multi(metrics: string[], title: string, extra: Partial<TemplateWidget> = {}): TemplateWidget {
	return {
		viz: 'multi',
		metric: metrics[0],
		metrics,
		title,
		span: 2,
		chartType: 'bar',
		granularity: 'day',
		rangeDays: 90,
		...extra
	};
}

/**
 * Builds a ranked top-N table widget.
 *
 * @param metric  - The metric key.
 * @param title   - The display title.
 * @param groupBy - The dimensions to split by.
 * @param extra   - Any overrides.
 * @returns The template widget.
 */
function table(metric: string, title: string, groupBy: string[], extra: Partial<TemplateWidget> = {}): TemplateWidget {
	return { viz: 'table', metric, title, span: 1, rangeDays: 90, groupBy, maxGroups: 10, ...extra };
}

/**
 * Builds a cohort-retention widget.
 *
 * @param title - The display title.
 * @param extra - Any overrides.
 * @returns The template widget.
 */
function cohort(title: string, extra: Partial<TemplateWidget> = {}): TemplateWidget {
	return { viz: 'cohort', metric: '', title, span: 3, rangeDays: 365, ...extra };
}

/**
 * Builds a full-width section header.
 *
 * @param title - The heading text.
 * @returns The template widget.
 */
function section(title: string): TemplateWidget {
	return { viz: 'section', metric: '', title, span: 1 };
}

/** The catalogue of pre-defined dashboard pages, in display order. */
export const dashboardTemplates: DashboardTemplate[] = [
	{
		id: 'overview',
		name: 'Overview',
		description: 'A high-level summary across accounts, sign-ins, applications and activity.',
		icon: 'fa-solid fa-chart-pie',
		columnCount: 4,
		widgets: [
			value('users.total', 'Total users'),
			value('users.active', 'Active accounts'),
			value('applications.total', 'Applications'),
			value('sessions.active', 'Active sessions'),
			section('Growth & activity'),
			chart('users.signups', 'New users', { span: 2 }),
			chart('sessions.created', 'Sign-ins', { span: 2 }),
			chart('events.count', 'Tracked actions', { span: 2 }),
			chart('errors.count', 'System errors', { span: 2 })
		]
	},
	{
		id: 'users',
		name: 'Users & Growth',
		description: 'The account base, signups, retention and how accounts are configured.',
		icon: 'fa-solid fa-users',
		columnCount: 3,
		widgets: [
			value('users.total', 'Total users'),
			value('users.signups', 'New users (90d)', { compare: true, sparkline: true }),
			value('events.uniqueUsers', 'Active users (90d)', { compare: true, sparkline: true }),
			section('Acquisition'),
			chart('users.signups', 'New users', { span: 2 }),
			table('users.signups', 'Signups by auth provider', ['authProvider']),
			multi(['users.signups', 'users.deleted'], 'Joined vs left', { span: 3 }),
			chart('users.cumulative', 'Users over time', {
				chartType: 'area',
				granularity: 'week',
				rangeDays: 365,
				span: 3
			}),
			section('Who they are'),
			table('users.total', 'Accounts by auth provider', ['authProvider']),
			table('users.total', 'Accounts by company', ['company']),
			table('users.total', 'Accounts by location', ['location']),
			table('users.total', 'Accounts by application', ['application']),
			table('users.total', 'Accounts by role', ['role']),
			table('users.total', 'Accounts by language', ['language']),
			section('Retention & churn'),
			cohort('Retention by signup month'),
			value('users.deleted', 'Accounts deleted (90d)', { compare: true, sparkline: true }),
			value('users.churnRate', 'Account churn rate'),
			value('users.returning', 'Returning users (90d)', { compare: true, sparkline: true })
		]
	},
	{
		id: 'authentication',
		name: 'Authentication',
		description: 'How people sign in, where from, and how they secure their accounts.',
		icon: 'fa-solid fa-key',
		columnCount: 3,
		widgets: [
			value('sessions.created', 'Sign-ins (90d)', { compare: true, sparkline: true }),
			value('sessions.active', 'Active sessions'),
			value('sessions.uniqueUsers', 'Signing-in users (90d)', { compare: true, sparkline: true }),
			section('Sign-in activity'),
			chart('sessions.created', 'Sessions started', { span: 2 }),
			chart('auth.registrations', 'Registrations'),
			table('sessions.created', 'Sign-ins by country', ['country']),
			table('sessions.created', 'Sign-ins by user', ['user']),
			table('users.total', 'Accounts by preferred sign-in', ['preferredAuthMethod']),
			section('Methods'),
			multi(['auth.otpRequests', 'auth.passkeyLogins', 'auth.socialLinks'], 'Sign-in methods used', { span: 3 }),
			value('passkeys.total', 'Registered passkeys'),
			chart('passkeys.registered', 'Passkeys registered'),
			table('passkeys.total', 'Passkeys by type', ['deviceType']),
			section('Account security'),
			value('users.twoFactor', 'Accounts with 2FA'),
			chart('auth.twoFactorEnabled', '2FA turned on'),
			chart('auth.twoFactorDisabled', '2FA turned off'),
			section('Recovery & changes'),
			chart('auth.passwordResets', 'Passwords reset'),
			chart('auth.emailChanges', 'Email addresses changed'),
			chart('auth.usernameChanges', 'Usernames changed')
		]
	},
	{
		id: 'applications',
		name: 'Applications & Access',
		description: 'Which applications people use, how they are registered, and OAuth traffic.',
		icon: 'fa-solid fa-cubes',
		columnCount: 3,
		widgets: [
			value('applications.total', 'Registered applications'),
			value('applications.activeRegistrations', 'Granted registrations'),
			value('roles.total', 'Roles'),
			section('Registrations'),
			chart('applications.registrations', 'New registrations', { span: 2 }),
			chart('applications.unregistrations', 'Unregistrations'),
			table('applications.registrations', 'Registrations by application', ['application']),
			table('applications.activeRegistrations', 'Granted by application', ['application']),
			table('roles.total', 'Roles by application', ['application']),
			section('OAuth'),
			chart('oauth.authorizations', 'Authorizations granted', { span: 2 }),
			value('oauth.activeRefreshTokens', 'Active refresh tokens'),
			table('oauth.codesIssued', 'Authorization codes by client', ['client']),
			table('oauth.refreshTokensIssued', 'Refresh tokens by client', ['client']),
			table('oauth.codesIssued', 'Authorizations by scope', ['scopes'])
		]
	},
	{
		id: 'newsletters',
		name: 'Newsletters & Email',
		description: 'Campaign delivery, engagement, and every transactional email the platform sends.',
		icon: 'fa-solid fa-envelope',
		columnCount: 3,
		widgets: [
			value('newsletters.subscribers', 'Subscribers'),
			value('newsletters.openRate', 'Open rate'),
			value('newsletters.clickRate', 'Click rate'),
			section('Delivery'),
			multi(['newsletters.sent', 'newsletters.opened', 'newsletters.clicks'], 'Sent, opened and clicked', {
				span: 3
			}),
			chart('newsletters.failed', 'Delivery failures'),
			table('newsletters.sent', 'Sent by campaign', ['newsletter']),
			table('newsletters.opened', 'Opened by campaign', ['newsletter']),
			table('newsletters.clicks', 'Most-clicked destinations', ['originalUrl'], { span: 2 }),
			section('List movement'),
			multi(['newsletters.subscriptions', 'newsletters.unsubscribes'], 'Sign-ups vs opt-outs', { span: 2 }),
			chart('newsletters.campaigns', 'Campaigns created'),
			section('All email'),
			chart('emails.sent', 'Emails sent', { span: 2 }),
			table('emails.sent', 'Emails by type', ['type'])
		]
	},
	{
		id: 'reliability',
		name: 'Reliability',
		description: 'Endpoint traffic, failures by route, and who is hitting errors.',
		icon: 'fa-solid fa-triangle-exclamation',
		columnCount: 3,
		widgets: [
			value('events.count', 'Actions (90d)', { compare: true, sparkline: true }),
			value('events.failed', 'Failed actions (90d)', { compare: true, sparkline: true }),
			value('events.errorRate', 'Action error rate'),
			section('Traffic'),
			chart('events.count', 'Tracked actions', { span: 2 }),
			chart('events.uniqueUsers', 'Acting users'),
			table('events.count', 'Actions by endpoint', ['name']),
			table('events.count', 'Actions by route', ['route']),
			table('events.count', 'Actions by HTTP method', ['method']),
			section('Failures'),
			chart('events.failed', 'Failed actions', { span: 2 }),
			chart('errors.count', 'System errors'),
			table('events.failed', 'Failures by endpoint', ['name']),
			table('events.failed', 'Failures by error', ['error']),
			table('errors.count', 'Errors by user', ['user']),
			section('Audit trail'),
			chart('audit.count', 'Audit entries', { span: 2 }),
			value('errors.rate', 'System error rate'),
			table('audit.count', 'Entries by action', ['action'], { span: 3 })
		]
	}
];
